      
import axios from 'axios'
import Map, { getLatLng } from 'components/Map'
import {
  useFetchFarmersQuery,
  useGenerateGeeDataMutation,
} from 'features/private-views/farmers-web/farmers-list/farmersListApiSlice'
import { getPublicVersion } from 'features/public-api/publicVersion/publicVersionAction'
import { capitalizeEveryWord } from 'helpers'
import moment from 'moment-timezone'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Navigation,
  Phone,
  RefreshCw,
  Share2,
  User,
  X,
} from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Input, Spinner } from 'reactstrap'
import { apiEndpoint } from 'utility/Utils'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import {
  API_FARMERS_PLOTTING,
  API_SHARED_GROUP_LANDPLOT,
  API_SHARED_GROUP_LANDPLOTPOINT,
} from 'utils/apiList'
import {
  useFetchSharedGroupsQuery,
  useFetchSharedGroupFarmersQuery,
} from 'features/private-views/farmers-web/shared-farmers/sharedFarmersApiSlice'
import FilterChips from './FilterChips'
import LayerPanel from './LayerPanel'
import {
  applyLayerRules,
  DEFAULT_LAYERS,
  ESG_METRICS,
} from './layers'
import MapLegend from './MapLegend'
import { DEFAULT_FILTERS, filterMembers, hasActiveFilters } from './memberFilters'
import ItemList, { aggregateItems } from './ItemList'
import Sidebar, {
  formatHa,
  initials,
  staticMapUrl,
  totalLandSize,
} from './Sidebar'
import useLayerData from './useLayerData'
import './spatial.css'

const spatialDataCache = {}
const plotPointsByCodeCache = {}
const plotsBySourceUserCache = {}

const MY_DATA = { value: 'my_data', label: 'Anggota Saya' }

const LAND_TYPES = {
  forest: '🌳 Hutan',
  agriculture: '🌾 Pertanian',
  urban: '🏡 Permukiman',
}
const landTypeLabel = (type) =>
  type ? LAND_TYPES[type.toLowerCase()] || capitalizeEveryWord(type) : ''

const groupLogo = (sg) => sg?.companyLogo || sg?.logo
const groupShortName = (sg) => sg?.companyShortName

const GroupAvatar = ({ sg, size }) => (
  <span
    className="gm-avatar"
    style={
      size
        ? { width: size, height: size, fontSize: Math.round(size * 0.36) }
        : undefined
    }
  >
    {groupShortName(sg)?.slice(0, 4) || initials(sg?.ownerName || 'SG')}
    {groupLogo(sg) ? (
      <img
        src={groupLogo(sg)}
        alt=""
        onError={(e) => {
          e.currentTarget.style.display = 'none'
        }}
      />
    ) : null}
  </span>
)

const getSourceKey = (source) => {
  if (!source) return null
  return source.value === 'my_data' ? 'my_data' : String(source.value)
}

export default function Spatial() {
  const dispatch = useDispatch()
  const { token } = useSelector((e) => e.auth)

  const [selectedDataSource, setSelectedDataSource] = useState(MY_DATA)
  const [enableSharedGroupsQuery, setEnableSharedGroupsQuery] = useState(false)
  const [isChoosingShared, setIsChoosingShared] = useState(false)
  const [searchSharedGroup, setSearchSharedGroup] = useState('')

  const { data: myData, isLoading: isLoadingMyData } = useFetchFarmersQuery(
    {
      size: 9999,
      status: 'JOIN',
    },
    { skip: !selectedDataSource || selectedDataSource?.value !== 'my_data' },
  )

  const { data: sharedGroupsData, isLoading: isLoadingSharedGroups } =
    useFetchSharedGroupsQuery(
      {
        page: 0,
        size: 100,
      },
      { skip: !enableSharedGroupsQuery },
    )

  const isShared = Boolean(
    selectedDataSource && selectedDataSource?.value !== 'my_data',
  )
  const sharedResourceId = isShared ? selectedDataSource?.value : null

  const { data: sharedData, isLoading: isLoadingSharedData } =
    useFetchSharedGroupFarmersQuery(
      {
        resourceId: sharedResourceId,
        size: 9999,
        status: 'JOIN',
      },
      { skip: !isShared || !sharedResourceId },
    )

  const allSharedGroups = useMemo(() => {
    return sharedGroupsData?.data?.items || sharedGroupsData?.items || []
  }, [sharedGroupsData])

  const filteredSharedGroups = useMemo(() => {
    if (!searchSharedGroup.trim()) return allSharedGroups
    const term = searchSharedGroup.toLowerCase()
    return allSharedGroups.filter(
      (sg) =>
        (sg.ownerName && sg.ownerName.toLowerCase().includes(term)) ||
        String(sg.resourceId).includes(term) ||
        (sg.role && sg.role.toLowerCase().includes(term)),
    )
  }, [allSharedGroups, searchSharedGroup])

  const activeData = isShared
    ? sharedData
    : selectedDataSource?.value === 'my_data'
      ? myData
      : null
  const activeLoading = isShared
    ? isLoadingSharedData
    : selectedDataSource?.value === 'my_data'
      ? isLoadingMyData
      : false

  const [
    generateGeeData,
    { data: geeData, isLoading: geeLoading, error: geeError },
  ] = useGenerateGeeDataMutation()

  const [plottingPoints, setPlottingPoints] = useState([])
  const [defaultCenterMap, setDefaultCenterMap] = useState({
    lat: -6.26055485,
    lng: 106.82934195,
  })
  const [farmerList, setFarmerList] = useState([])
  const [loadingPlotting, setLoadingPlotting] = useState(false)
  const [selectedCode, setSelectedCode] = useState('')
  const [isSidebarVisible, setSidebarVisible] = useState(true)
  const [selectedLand, setSelectedLand] = useState({})
  const [showDetailLand, setShowDetailLand] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const rootRef = useRef(null)
  const [rootHeight, setRootHeight] = useState('100vh')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [allMapData, setAllMapData] = useState([])
  const [publicVersion, setPublicVersion] = useState(false)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [plotProgress, setPlotProgress] = useState(null)
  const [layers, setLayers] = useState(DEFAULT_LAYERS)
  const changeLayers = (next) => setLayers((prev) => applyLayerRules(prev, next))
  const [zoom, setZoom] = useState(10)
  const [esgMonth, setEsgMonth] = useState(moment().format('YYYY-MM-DD'))
  const [esgMetric, setEsgMetric] = useState(ESG_METRICS[0].value)
  const polygonLoadRef = useRef(null)
  const sourceKeyRef = useRef(null)
  sourceKeyRef.current = getSourceKey(selectedDataSource)
  const [detailTab, setDetailTab] = useState('overview')

  const closeDetail = () => {
    setShowDetailLand(false)
    setSelectedCode('')
    setSelectedUserId('')
    setSelectedLand({})
  }

  const selectSource = (opt) => {
    setIsChoosingShared(false)
    setSearchSharedGroup('')
    if (getSourceKey(opt) === getSourceKey(selectedDataSource)) return
    const cached = spatialDataCache[getSourceKey(opt)]
    setFarmerList(cached?.farmerList || [])
    setPlottingPoints(cached?.plottingPoints || [])
    setAllMapData(cached?.allMapData || [])
    if (cached?.defaultCenterMap) {
      setDefaultCenterMap(cached.defaultCenterMap)
    }
    setSelectedDataSource(opt)
    setPlotProgress(null)
    setFilters(DEFAULT_FILTERS)
    setSearch('')
    closeDetail()
  }

  const onSelectFarmer = (farmer) => {
    loadPolygons(farmer?.userId)
    setSelectedUserId(farmer?.userId)
    setSelectedCode('')
    setSelectedLand({})
    setDetailTab('overview')
    setShowDetailLand(true)
    if (isMobile) setSidebarVisible(true)
  }

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      const el = rootRef.current
      if (!el) return
      const bottom = Math.min(
        window.innerHeight,
        el.parentElement?.getBoundingClientRect().bottom || window.innerHeight,
      )
      const top = el.getBoundingClientRect().top
      setRootHeight(`${Math.max(bottom - top, 320)}px`)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (!activeData || !selectedDataSource) {
      return
    }
    const currentKey = getSourceKey(selectedDataSource)
    const isCached = Boolean(
      currentKey && spatialDataCache[currentKey]?.farmerList?.length > 0,
    )

    let isMounted = true

    const getCombinedData = async () => {
      const response = await combinedData(isCached, currentKey)
      if (isMounted && response && sourceKeyRef.current === currentKey) {
        setAllMapData(response?.updatedFlattenedData)
      }
    }

    getCombinedData()

    return () => {
      isMounted = false
    }
  }, [activeData, selectedDataSource?.value])

  useEffect(() => {
    fetchVersion()
  }, [])

  useEffect(() => {
    if (layers.parcels && plottingPoints.length) loadPolygons()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers.parcels, plottingPoints.length, selectedDataSource?.value])

  const fetchVersion = async () => {
    try {
      const res = await dispatch(getPublicVersion())
      setPublicVersion(res?.payload)
    } catch {
      setPublicVersion(false)
    }
  }

  // Polygons (landplotpoint) are loaded lazily by loadPolygons; reuse any already cached.
  const withCachedPolygons = (list) =>
    list.map((el) =>
      plotPointsByCodeCache[el.code]
        ? { ...el, plottingPoints: plotPointsByCodeCache[el.code] }
        : el,
    )

  const combinedData = async (isBackground = false, targetKey = null) => {
    if (!activeData) return
    const activeKey = targetKey || getSourceKey(selectedDataSource)
    const isActive = () => sourceKeyRef.current === activeKey
    if (!isBackground) {
      setLoadingPlotting(true)
    }
    const items = activeData?.data?.items || activeData?.items || []
    if (!isBackground && isActive()) {
      setFarmerList(items.map((el) => ({ ...el, open: false })))
    }
    const results = await Promise.all(items.map((el) => fetchPlot(el)))
    const flattenedData = withCachedPolygons(flattenData(results))
    const modifiedResults = results.map((result) => ({
      ...result,
      open: false,
    }))

    if (activeKey) {
      spatialDataCache[activeKey] = {
        farmerList: modifiedResults,
        plottingPoints: flattenedData,
        allMapData: flattenedData,
        defaultCenterMap,
      }
    }

    if (isActive()) {
      setPlottingPoints(flattenedData)
      setFarmerList(modifiedResults)
    }
    if (!isBackground) {
      setLoadingPlotting(false)
    }

    return {
      updatedFlattenedData: flattenedData,
      modifiedResults,
    }
  }

  const mergePolygons = (key, updated) => {
    const byCode = Object.fromEntries(
      updated.map((el) => [el.code, el.plottingPoints]),
    )
    const apply = (list) =>
      (list || []).map((el) =>
        byCode[el.code] ? { ...el, plottingPoints: byCode[el.code] } : el,
      )
    const cached = spatialDataCache[key]
    if (cached) {
      cached.plottingPoints = apply(cached.plottingPoints)
      cached.allMapData = apply(cached.allMapData)
    }
    if (sourceKeyRef.current === key) setPlottingPoints(apply)
  }

  // Without userId: every plot (Lahan layer). With userId: only that member's plots.
  const loadPolygons = async (userId) => {
    const key = getSourceKey(selectedDataSource)
    const targets = plottingPoints.filter(
      (el) => !el.plottingPoints && (!userId || el.userId === userId),
    )
    if (!targets.length || (!userId && polygonLoadRef.current === key)) return
    if (!userId) {
      polygonLoadRef.current = key
      setPlotProgress({ done: 0, total: targets.length })
    }
    const updated = await updateFlattenedDataWithApiResults(
      targets,
      Boolean(userId),
      (done) => {
        mergePolygons(key, done.slice(-1))
        if (!userId && sourceKeyRef.current === key) {
          setPlotProgress({ done: done.length, total: targets.length })
        }
      },
    )
    if (!userId) {
      polygonLoadRef.current = null
      if (sourceKeyRef.current === key) setPlotProgress(null)
    }
    return updated
  }

  const updateFlattenedDataWithApiResults = async (
    flattenedData,
    isBackground = false,
    onProgress,
  ) => {
    const updatedData = []
    if (!isBackground) {
      setLoadingPlotting(true)
    }
    for (const item of flattenedData) {
      const updatedItem = await fetchDataForItem(item)
      updatedData.push(updatedItem)
      onProgress?.(updatedData)

      if (!plotPointsByCodeCache[item.code]) {
        await new Promise((resolve) =>
          setTimeout(resolve, isBackground ? 100 : 500),
        )
      }
    }
    if (!isBackground) {
      setLoadingPlotting(false)
    }
    return updatedData
  }

  const fetchPlot = async (item) => {
    const cacheKey = `${isShared ? sharedResourceId : 'my'}_${item?.userId}`
    try {
      const plotUrl = isShared
        ? `${apiEndpoint}/${API_SHARED_GROUP_LANDPLOT}/${sharedResourceId}?userId=${item?.userId}`
        : `${apiEndpoint}/${API_FARMERS_PLOTTING}?userId=${item?.userId}`

      const { data } = await axios.get(plotUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      const plotItems = data?.data?.items || data?.items || []
      plotsBySourceUserCache[cacheKey] = plotItems
      return { ...item, plots: plotItems }
    } catch {
      if (plotsBySourceUserCache[cacheKey]) {
        return { ...item, plots: plotsBySourceUserCache[cacheKey] }
      }
      return { ...item, plots: [] }
    }
  }

  const fetchDataForItem = async (item) => {
    const code = item.code
    if (code && plotPointsByCodeCache[code]?.length > 0) {
      return {
        ...item,
        plottingPoints: plotPointsByCodeCache[code],
      }
    }
    try {
      const page = 0
      const plottingPoints = []

      const fetchPlottingData = async (code, page) => {
        try {
          const url = isShared
            ? `${apiEndpoint}/${API_SHARED_GROUP_LANDPLOTPOINT}/${sharedResourceId}?code=${code}&sortby=id&sortdir=asc&page=${page}&size=150`
            : `${apiEndpoint}/api/v1/bussines/landplotpoint?code=${code}&sortby=id&sortdir=asc&page=${page}&size=150`

          const { data } = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          const newData = data?.data?.items || data?.items || []
          plottingPoints.push(...newData)

          const currentPage = data?.data?.currentPage ?? data?.currentPage
          const totalPages = data?.data?.totalPages ?? data?.totalPages
          if (currentPage < totalPages && totalPages > 1) {
            await fetchPlottingData(code, page + 1)
          }
        } catch (error) {
          console.error('Error fetching plotting data:', error)
        }
      }

      await fetchPlottingData(code, page)

      if (code && plottingPoints.length > 0) {
        plotPointsByCodeCache[code] = plottingPoints
      }

      return {
        ...item,
        plottingPoints,
      }
    } catch (error) {
      console.error(
        'Error fetching data for item:',
        error?.response?.data || error,
      )
      return item
    }
  }

  const flattenData = (data) => {
    const result = []
    data.forEach((farmer) => {
      if (farmer.plots && farmer.plots.length > 0) {
        farmer.plots.forEach((plot) => {
          result.push({
            ...plot,
            userId: farmer.userId,
            name: farmer.name,
            phoneNumber: farmer.phoneNumber,
            status: farmer.status,
            code: plot.code,
            userLevel: farmer.userLevel,
            locationPoint: plot.locationPoint,
            landName: plot.landName,
            businessName: plot.businessName,
            location: plot.location,
            createdAt: plot.createdAt,
            startRecordTime: plot.startRecordTime,
            endRecordTime: plot.endRecordTime,
            size: plot.size,
          })
        })
      }
    })
    return result
  }

  const toggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible)
  }

  const handleGenerate = async () => {
    try {
      const result = await generateGeeData({
        plottingPointId: selectedLand?.id,
      }).unwrap()
      const updatedGeeData = {
        ...result,
        maps:
          result.maps?.map((mapObj) => ({
            ...mapObj,
            show: true,
          })) || [],
      }

      setSelectedLand((prevData) => ({
        ...prevData,
        geeData: updatedGeeData,
      }))
      setFarmerList((prevData) =>
        prevData.map((user) => {
          if (user.userId !== selectedUserId) return user

          return {
            ...user,
            plots: user.plots.map((plotItem) => {
              if (plotItem.id !== selectedLand?.id) return plotItem
              return {
                ...plotItem,
                geeData: { ...result },
              }
            }),
          }
        }),
      )
    } catch (err) {
      console.error('failed:', err)
    }
  }

  const onSelectLand = (item) => {
    const newItem = {
      ...item,
      geeData: {
        ...item.geeData,
        maps:
          item.geeData?.maps?.map((mapObj) => ({
            ...mapObj,
            show: true,
          })) || [],
      },
    }

    loadPolygons(item?.userId)
    setSelectedLand(newItem)
    setShowDetailLand(true)
    setSelectedCode(item?.code)
    if (item?.userId) setSelectedUserId(item.userId)
    setDetailTab('lahan')
    // Focused on one land: desktop shows only the detail panel; mobile keeps the sheet open
    setSidebarVisible(isMobile)
  }

  function handleToggle(key) {
    if (!selectedLand || !selectedLand.geeData || !selectedLand.geeData.maps)
      return

    const newMaps = [...selectedLand.geeData.maps]

    const index = newMaps.findIndex((mapItem) => {
      if (!mapItem.layerName) return false
      if (key === 'forest') return mapItem.layerName.startsWith('Forest ')
      if (key === 'reforestation') return mapItem.layerName === 'Forest Gain'
      if (key === 'deforestation') return mapItem.layerName === 'Forest Loss'
      return false
    })

    if (index !== -1) {
      newMaps[index] = {
        ...newMaps[index],
        show: !newMaps[index].show,
      }

      setSelectedLand({
        ...selectedLand,
        geeData: {
          ...selectedLand.geeData,
          maps: newMaps,
        },
      })
    }
  }

  const currentKey = getSourceKey(selectedDataSource)
  const hasCachedData = Boolean(
    currentKey && spatialDataCache[currentKey]?.farmerList?.length > 0,
  )
  const isDataLoading = !hasCachedData && (loadingPlotting || activeLoading)

  const isFiltered = hasActiveFilters(filters)
  const filteredFarmers = useMemo(
    () => (isFiltered ? filterMembers(farmerList, filters) : farmerList),
    [farmerList, filters, isFiltered],
  )
  const mapData = useMemo(() => {
    if (!isFiltered) return plottingPoints || []
    const ids = new Set(filteredFarmers.map((el) => el?.userId))
    return (plottingPoints || []).filter((el) => ids.has(el?.userId))
  }, [plottingPoints, filteredFarmers, isFiltered])

  const { colorFor, overlays, heatmap, status, disabledReason, refreshEsg } =
    useLayerData({
      layers,
      isShared,
      sharedResourceId,
      members: filteredFarmers,
      mapData,
      zoom,
      esgMonth,
      esgMetric,
    })

  const selectedFarmer = farmerList.find((el) => el?.userId === selectedUserId)
  const farmerMapItems = selectedUserId
    ? plottingPoints.filter((el) => el?.userId === selectedUserId)
    : []
  const selectedMapItem = farmerMapItems.find((el) => el?.code === selectedCode)
  const detailOpen = showDetailLand && Boolean(selectedFarmer)
  const focusCodes = !detailOpen
    ? []
    : selectedCode
      ? [selectedCode]
      : farmerMapItems.map((el) => el?.code)
  const focusLatLng = getLatLng(
    selectedMapItem || farmerMapItems[0] || selectedFarmer?.plots?.[0],
  )
  const heroUrl = staticMapUrl(
    selectedMapItem
      ? [selectedMapItem]
      : farmerMapItems.length
        ? farmerMapItems
        : selectedFarmer?.plots || [],
    '408x220',
  )

  const PANEL_W = 408
  const panelCount = (isSidebarVisible ? 1 : 0) + (detailOpen ? 1 : 0)
  const panelsRight = panelCount * (PANEL_W + 16)
  const mapLeft = isMobile ? 16 : Math.max(panelsRight, 0) + 16

  const sourcePicker = (
    <>
      <button
        type="button"
        className={`gm-chip ${!isShared ? 'gm-chip--active' : ''}`}
        onClick={() => selectSource(MY_DATA)}
        aria-pressed={!isShared}
        data-testid="select-source-my-data-card"
      >
        <User size={16} />
        Anggota Saya
      </button>
      <div className="gm-chip-wrap">
        <button
          type="button"
          className={`gm-chip ${isShared ? 'gm-chip--active' : ''}`}
          onClick={() => {
            setEnableSharedGroupsQuery(true)
            setIsChoosingShared((v) => !v)
          }}
          aria-pressed={isShared}
          aria-expanded={isChoosingShared}
          data-testid="select-source-shared-card"
        >
          {isShared && groupLogo(selectedDataSource?.data) ? (
            <GroupAvatar sg={selectedDataSource.data} size={20} />
          ) : (
            <Share2 size={16} />
          )}
          {isShared
            ? groupShortName(selectedDataSource?.data) ||
              selectedDataSource?.label
            : 'Anggota Bersama'}
          <ChevronDown size={14} />
        </button>
        {isChoosingShared ? (
          <>
            <div
              className="gm-menu-backdrop"
              onClick={() => setIsChoosingShared(false)}
            />
            <div className="gm-menu spatial-scrollbar" role="menu">
              {allSharedGroups.length > 3 ? (
                <Input
                  type="text"
                  placeholder="Cari sumber grup bersama..."
                  value={searchSharedGroup}
                  onChange={(e) => setSearchSharedGroup(e.target.value)}
                  className="form-control-sm mb-2"
                  autoFocus
                />
              ) : null}
              {isLoadingSharedGroups ? (
                <div className="d-flex align-items-center gap-2 p-2">
                  <Spinner color="primary" size="sm" />
                  <span className="gm-muted">Memuat sumber grup...</span>
                </div>
              ) : filteredSharedGroups.length ? (
                filteredSharedGroups.map((sg) => (
                  <button
                    type="button"
                    role="menuitem"
                    key={sg?.resourceId}
                    className={`gm-menu-item ${
                      String(sg?.resourceId) ===
                      String(selectedDataSource?.value)
                        ? 'gm-menu-item--active'
                        : ''
                    }`}
                    onClick={() =>
                      selectSource({
                        value: sg.resourceId,
                        label: sg.ownerName || `ID ${sg.resourceId}`,
                        data: sg,
                      })
                    }
                    data-testid={`select-shared-group-item-${sg?.resourceId}`}
                  >
                    <GroupAvatar sg={sg} />
                    <span className="d-flex flex-column text-start">
                      <span className="gm-menu-title">
                        {sg?.ownerName || `Sumber #${sg?.resourceId}`}
                      </span>
                      <span className="gm-muted" style={{ fontSize: 12 }}>
                        {groupShortName(sg) ? (
                          <>{groupShortName(sg)} &bull; </>
                        ) : null}
                        Peran: {sg?.role || 'VIEWER'}
                      </span>
                    </span>
                  </button>
                ))
              ) : (
                <div className="p-2 gm-muted">
                  {searchSharedGroup
                    ? 'Tidak ada sumber grup yang sesuai dengan pencarian.'
                    : 'Belum ada sumber grup yang dibagikan kepada Anda.'}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </>
  )

  return (
    <div
      ref={rootRef}
      style={{ display: 'flex', height: rootHeight, width: '100%' }}
    >
      <div className="gm-shell" style={{ '--gm-map-left': `${mapLeft}px` }}>
        {publicVersion?.googleEarthEngine &&
        selectedLand?.geeData?.maps?.length ? (
          <LegendColor />
        ) : null}
        <MapLegend
          layers={layers}
          disabledReason={disabledReason}
          status={status}
          heatmap={heatmap}
          esgMetric={esgMetric}
        />
        <div className="gm-map-area">
          {loadingPlotting || activeLoading ? (
            <div className="gm-progress" />
          ) : null}
          <Map
            dataMap={
              publicVersion?.googleEarthEngine
                ? selectedLand?.geeData?.maps
                : []
            }
            data={mapData}
            center={defaultCenterMap}
            multiple
            selectedCodes={focusCodes}
            fitPadding={
              isMobile
                ? {
                    top: 140,
                    right: 40,
                    bottom:
                      (detailOpen
                        ? Math.round(window.innerHeight * 0.5)
                        : isSidebarVisible
                          ? 120
                          : 0) + 40,
                    left: 40,
                  }
                : { top: 80, right: 60, bottom: 60, left: mapLeft + 40 }
            }
            onSelectLand={onSelectLand}
            onShowDetailLand={() => setShowDetailLand(true)}
            layers={layers}
            colorFor={colorFor}
            overlays={overlays}
            heatmap={heatmap}
            onZoomLevel={setZoom}
            controls={
              <LayerPanel
                layers={layers}
                onChange={changeLayers}
                disabledReason={disabledReason}
                status={status}
                parcelsProgress={plotProgress}
                esgMonth={esgMonth}
                onEsgMonth={setEsgMonth}
                onEsgRefresh={refreshEsg}
                esgMetric={esgMetric}
                onEsgMetric={setEsgMetric}
              />
            }
          />
        </div>

        <Sidebar
          farmerList={filteredFarmers}
          isFiltered={isFiltered}
          selectedUserId={selectedUserId}
          onSelectFarmer={onSelectFarmer}
          isSidebarVisible={isSidebarVisible}
          onToggleSidebar={toggleSidebar}
          loading={isDataLoading}
          search={search}
          isMobile={isMobile}
          sourcePicker={sourcePicker}
          detail={detailOpen && isMobile ? renderDetail() : null}
          detailKey={selectedUserId}
          onSearchChange={(v) => {
            setSearch(v)
            if (v && !isSidebarVisible) setSidebarVisible(true)
          }}
        />

        {!isMobile ? (
          <button
            type="button"
            className="gm-collapse-tab"
            style={{ left: panelsRight }}
            onClick={toggleSidebar}
            aria-label={isSidebarVisible ? 'Tutup daftar' : 'Buka daftar'}
          >
            {isSidebarVisible ? (
              <ChevronLeft size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
        ) : null}

        <div
          className="gm-chips"
          style={
            isMobile
              ? undefined
              : { left: Math.max(panelsRight, PANEL_W + 16) + 16 }
          }
        >
          <FilterChips
            farmerList={farmerList}
            filters={filters}
            onChange={setFilters}
          />
        </div>

        {detailOpen && !isMobile ? (
          <div
            key={selectedUserId}
            className="gm-place"
            style={
              isSidebarVisible ? { left: PANEL_W + 32 } : { left: 16, top: 80 }
            }
            role="dialog"
            aria-label={selectedFarmer?.name}
          >
            {renderDetail()}
          </div>
        ) : null}
      </div>
    </div>
  )

  function renderDetail() {
    const plots = selectedFarmer?.plots || []
    const items = aggregateItems(plots)
    const coords = focusLatLng
      ? `${focusLatLng.lat.toFixed(6)}, ${focusLatLng.lng.toFixed(6)}`
      : ''
    return (
      <>
        {isMobile ? (
          <button
            type="button"
            className="gm-place-back"
            onClick={closeDetail}
            aria-label="Kembali ke daftar"
          >
            <ArrowLeft size={20} />
          </button>
        ) : (
          <button
            type="button"
            className="gm-place-close"
            onClick={closeDetail}
            aria-label="Tutup"
          >
            <X size={18} />
          </button>
        )}
        {heroUrl ? (
          <img
            className="gm-place-hero"
            src={heroUrl}
            alt=""
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : null}

        <div className="gm-place-head">
          <h1 className="gm-place-title">{selectedFarmer?.name || '-'}</h1>
          {selectedFarmer?.status ? (
            <span className="gm-badge">
              {capitalizeEveryWord(String(selectedFarmer.status))}
            </span>
          ) : null}
          <div className="gm-place-stats">
            {[
              ['Perkiraan luas', totalLandSize(plots)],
              ['Lahan', newFormatThousandSeparator(plots.length)],
              ['Jenis barang', newFormatThousandSeparator(items.length)],
            ].map(([label, value]) => (
              <div key={label} className="gm-summary-stat">
                <span className="gm-summary-value">{value}</span>
                <span className="gm-summary-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {focusLatLng ? (
          <div className="gm-place-actions">
            <a
              className="gm-action gm-action--primary"
              href={`https://www.google.com/maps/dir/?api=1&destination=${focusLatLng.lat},${focusLatLng.lng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="gm-action-icon gm-action-icon--primary">
                <Navigation size={18} />
              </span>
              Rute
            </a>
          </div>
        ) : null}

        <div className="gm-tabs" role="tablist">
          {[
            ['overview', 'Ringkasan'],
            ['lahan', `Lahan (${plots.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={detailTab === key}
              className={`gm-tab ${detailTab === key ? 'gm-tab--active' : ''}`}
              onClick={() => setDetailTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {detailTab === 'overview' ? (
          <>
            <div className="gm-place-rows">
              <div className="gm-row">
                <Phone size={20} />
                <span className="gm-row-text">
                  <span className="gm-row-label">Telepon</span>
                  {selectedFarmer?.phoneNumber || '-'}
                </span>
              </div>
              <div className="gm-row">
                <MapPin size={20} />
                <span className="gm-row-text">
                  <span className="gm-row-label">Koordinat</span>
                  {coords || 'Belum ada lokasi'}
                </span>
              </div>
            </div>
            <div className="gm-place-section">
              <div className="gm-section-title">Barang di lahan</div>
              <ItemList
                items={items}
                showPlotCount
                empty="Belum ada barang tercatat di lahan anggota ini."
              />
            </div>
          </>
        ) : (
          <div className="gm-place-rows">
            {!plots.length ? (
              <div className="gm-row gm-muted">Belum ada lahan tercatat</div>
            ) : (
              plots.map((plot) => {
                const isOpen = plot?.code === selectedCode
                const itemCount = aggregateItems([plot]).length
                return (
                  <div key={plot?.code}>
                    <button
                      type="button"
                      className={`gm-land ${isOpen ? 'gm-land--active' : ''}`}
                      onClick={() =>
                        isOpen
                          ? setSelectedCode('')
                          : onSelectLand({
                              ...plot,
                              userId: selectedFarmer.userId,
                            })
                      }
                      aria-expanded={isOpen}
                    >
                      <MapPin size={20} />
                      <span className="gm-land-body">
                        <span className="gm-menu-title">
                          {plot?.landName || '-'}
                        </span>
                        <span className="gm-muted" style={{ fontSize: 13 }}>
                          {[
                            formatHa(plot?.size),
                            landTypeLabel(plot?.geeData?.landType),
                            itemCount ? `${itemCount} barang` : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      {plot?.geeData?.eudr ? (
                        <img
                          src="https://siph.groupesifca.com/images/2024/01/11/logo-complainte-eudr__plan-de-travail-1.png"
                          alt="EUDR"
                          style={{ maxWidth: '40px' }}
                        />
                      ) : null}
                      <ChevronDown size={18} className="gm-land-chevron" />
                    </button>
                    {isOpen ? <LandDetail /> : null}
                  </div>
                )
              })
            )}
          </div>
        )}
      </>
    )
  }

  function LandDetail() {
    const gee = selectedLand?.geeData
    const forest = gee?.dump?.forest
    const diff = forest - gee?.dump?.forestOld
    const hasDiff = !isNaN(diff) && diff !== 0
    const showForest = publicVersion?.googleEarthEngine || gee?.landType
    return (
      <div className="gm-land-detail">
        <div className="gm-place-section">
          <div className="gm-section-title">Barang</div>
          <ItemList
            items={aggregateItems([selectedLand])}
            empty="Belum ada barang tercatat di lahan ini."
          />
        </div>

        {showForest ? (
          <>
            <div className="gm-place-section">
              <div className="gm-section-title">Tutupan hutan</div>
              <div className="gm-place-stats">
                <div className="gm-summary-stat">
                  <span className="gm-summary-value">
                    {landTypeLabel(gee?.landType) || '-'}
                  </span>
                  <span className="gm-summary-label">Jenis lahan</span>
                </div>
                <div className="gm-summary-stat">
                  <span className="gm-summary-value">
                    {isNaN(forest)
                      ? '-'
                      : `${newFormatThousandSeparator(forest)}%`}
                  </span>
                  <span className="gm-summary-label">Tutupan hutan</span>
                </div>
                <div className="gm-summary-stat">
                  <span
                    className={`gm-summary-value ${
                      hasDiff ? (diff > 0 ? 'gm-up' : 'gm-down') : ''
                    }`}
                  >
                    {hasDiff ? (
                      diff > 0 ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : null}
                    {isNaN(diff)
                      ? '-'
                      : `${newFormatThousandSeparator(Math.abs(diff))}%`}
                  </span>
                  <span className="gm-summary-label">Perubahan</span>
                </div>
              </div>
            </div>

            <div className="gm-place-section">
              <div className="gm-section-title">Riwayat analisis</div>
              {gee?.history?.length ? (
                <table className="gm-table">
                  <thead>
                    <tr>
                      <th>Tanggal</th>
                      <th>Jenis lahan</th>
                      <th>Hutan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gee.history.map((a, index) => (
                      <tr key={index}>
                        <td>{moment(a?.createdAt).format('DD MMM YYYY')}</td>
                        <td>{landTypeLabel(a?.landType) || '-'}</td>
                        <td>
                          {typeof a?.forest === 'number'
                            ? `${newFormatThousandSeparator(a.forest)}%`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="gm-muted">
                  Belum ada analisis
                  {publicVersion?.googleEarthEngine
                    ? '. Klik Perbarui untuk menganalisis lahan ini.'
                    : '.'}
                </div>
              )}
            </div>

            <div className="gm-place-footer">
              {publicVersion?.googleEarthEngine ? (
                <button
                  type="button"
                  className="gm-pill"
                  onClick={handleGenerate}
                  disabled={
                    !selectedLand?.id ||
                    !selectedLand?.size ||
                    parseFloat(selectedLand?.size) === 0 ||
                    geeLoading
                  }
                >
                  {geeLoading ? <Spinner size="sm" /> : <RefreshCw size={16} />}
                  Perbarui
                </button>
              ) : null}
              <span className="ms-auto d-flex align-items-center gap-1">
                <img
                  src="https://earthengine.google.com/static/images/earth_engine_icon.png"
                  alt=""
                  style={{ maxWidth: '20px' }}
                />
                Powered by Landsat
              </span>
            </div>
          </>
        ) : null}
      </div>
    )
  }

  function LegendColor() {
    return (
      <div className="gm-legend">
        <div style={{ marginBottom: 6, fontWeight: 'bold' }}>
          Keterangan Warna:
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 4,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#40b062',
                borderRadius: 4,
                opacity:
                  selectedLand?.geeData?.maps?.[0]?.layerName?.startsWith(
                    'Forest ',
                  ) && selectedLand?.geeData?.maps?.[0]?.show
                    ? 1
                    : 0.3,
              }}
            />
            <span
              style={{
                color:
                  selectedLand?.geeData?.maps?.[0]?.layerName?.startsWith(
                    'Forest ',
                  ) && selectedLand?.geeData?.maps?.[0]?.show
                    ? '#000'
                    : '#999',
              }}
            >
              Forest
            </span>
          </div>
          <Input
            style={{ cursor: 'pointer' }}
            type="checkbox"
            checked={
              selectedLand?.geeData?.maps?.[0]?.layerName?.startsWith(
                'Forest ',
              ) && selectedLand?.geeData?.maps?.[0]?.show
            }
            onChange={() => handleToggle('forest')}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 4,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#4ad86b',
                borderRadius: 4,
                opacity:
                  selectedLand?.geeData?.maps?.[2]?.layerName ===
                    'Forest Gain' && selectedLand?.geeData?.maps?.[2]?.show
                    ? 1
                    : 0.3,
              }}
            />
            <span
              style={{
                color:
                  selectedLand?.geeData?.maps?.[2]?.layerName ===
                    'Forest Gain' && selectedLand?.geeData?.maps?.[2]?.show
                    ? '#000'
                    : '#999',
              }}
            >
              Reforestation
            </span>
          </div>
          <Input
            style={{ cursor: 'pointer' }}
            type="checkbox"
            checked={
              selectedLand?.geeData?.maps?.[2]?.layerName === 'Forest Gain' &&
              selectedLand?.geeData?.maps?.[2]?.show
            }
            onChange={() => handleToggle('reforestation')}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#df5758',
                borderRadius: 4,
                opacity:
                  selectedLand?.geeData?.maps?.[1]?.layerName ===
                    'Forest Loss' && selectedLand?.geeData?.maps?.[1]?.show
                    ? 1
                    : 0.3,
              }}
            />
            <span
              style={{
                color:
                  selectedLand?.geeData?.maps?.[1]?.layerName ===
                    'Forest Loss' && selectedLand?.geeData?.maps?.[1]?.show
                    ? '#000'
                    : '#999',
              }}
            >
              Deforestation
            </span>
          </div>
          <Input
            style={{ cursor: 'pointer' }}
            type="checkbox"
            checked={
              selectedLand?.geeData?.maps?.[1]?.layerName === 'Forest Loss' &&
              selectedLand?.geeData?.maps?.[1]?.show
            }
            onChange={() => handleToggle('deforestation')}
          />
        </div>
      </div>
    )
  }
}

    

    